import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { TOAST_LIST_ID } from '../../../services/toast/toast.service';

/** Modals that are open right now, newest last. Escape only closes the top one. */
const openModals: ModalComponent[] = [];

/** Add an id to an element's aria-owns list, keeping the ids already there. */
export function ariaOwnsAdd(element: Element | null | undefined, id: string): void {
  if (!element || !id) return;
  const ids = (element.getAttribute('aria-owns') ?? '').split(/\s+/).filter(Boolean);
  if (!ids.includes(id)) element.setAttribute('aria-owns', [...ids, id].join(' '));
}

/** Remove an id from an element's aria-owns list. */
export function ariaOwnsRemove(element: Element | null | undefined, id: string): void {
  if (!element || !id) return;
  const ids = (element.getAttribute('aria-owns') ?? '').split(/\s+/).filter((item) => item && item !== id);
  if (ids.length) element.setAttribute('aria-owns', ids.join(' '));
  else element.removeAttribute('aria-owns');
}

/**
 * Toasts render on <body>, outside every dialog. Screen readers that honour
 * aria-modal hide what is outside the dialog, so the top-most dialog claims
 * the toast list with aria-owns while it is open.
 */
function syncToastOwner(): void {
  openModals.forEach((modal, index) => {
    const dialog = modal.dialogElement;
    if (index === openModals.length - 1) ariaOwnsAdd(dialog, TOAST_LIST_ID);
    else ariaOwnsRemove(dialog, TOAST_LIST_ID);
  });
}

/**
 * The dimmed layer every dialog in the console sits on. It centres whatever
 * is projected into it, keeps keyboard focus inside while open, and hands
 * focus back to the trigger on close.
 *
 * While open, the layer is moved to <body> (as app-dropdown does with its
 * menu), so no ancestor stacking context, transform or overflow can trap it
 * under the page chrome. It carries the `.dark` class with it so the bk-*
 * tokens still switch.
 *
 * It never closes itself: Escape (and, when `closeOnBackdrop` is set, a click
 * on the dimmed area) emits `dismiss`, and the parent decides what closing
 * means — the same handler its own Close button already calls.
 *
 * Styles are global (ViewEncapsulation.None, `bk-modal*` names) so the gutter
 * and max-width rules reach the projected panel.
 */
@Component({
  selector: 'app-modal-component',
  standalone: true,
  imports: [CdkTrapFocus],
  templateUrl: './modal-component.component.html',
  styleUrl: './modal-component.component.css',
  encapsulation: ViewEncapsulation.None,
})
export class ModalComponent implements OnChanges, OnDestroy {
  @Input() className: string = '';
  @Input({ required: true }) open: boolean = false;

  /** Accessible name, for content that has no heading to point at. */
  @Input() label: string = '';
  /** id of the element that names the dialog (usually its heading). */
  @Input() labelledBy: string | null = null;
  /** id(s) of the element(s) that describe the dialog, e.g. a confirm's consequence text. */
  @Input() describedBy: string | null = null;
  /** 'alertdialog' for confirms that interrupt with something that needs an answer. */
  @Input() dialogRole: 'dialog' | 'alertdialog' = 'dialog';
  /** Emit `dismiss` when the dimmed area around the panel is clicked. */
  @Input() closeOnBackdrop: boolean = false;

  /** Escape on the top-most open modal, or a backdrop click when enabled. */
  @Output() dismiss = new EventEmitter<void>();

  private readonly document = inject(DOCUMENT);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /** The overlay element while it sits on <body>. */
  private portaled: HTMLElement | null = null;
  /** The role="dialog" element while open. */
  dialogElement: HTMLElement | null = null;

  /** Where the current pointer press started, so a drag out of the panel does not close it. */
  private pressStartedOnBackdrop = false;

  @ViewChild('overlay') set overlayRef(ref: ElementRef<HTMLElement> | undefined) {
    const element = ref?.nativeElement ?? null;
    if (element === this.portaled) return;
    this.portaled = element;
    if (!element || !this.isBrowser) return;
    // Read the theme where the dialog was declared, before it leaves that wrapper.
    element.classList.toggle('dark', !!this.host.nativeElement.closest('.dark'));
    this.document.body.appendChild(element);
  }

  @ViewChild('dialog') set dialogRef(ref: ElementRef<HTMLElement> | undefined) {
    this.dialogElement = ref?.nativeElement ?? null;
    if (this.dialogElement && this.isBrowser) syncToastOwner();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']) {
      this.open ? this.register() : this.unregister();
    }
  }

  ngOnDestroy(): void {
    this.unregister();
    // Destroyed while open: Angular removes the host, but the overlay now
    // lives on <body>, so take it out too.
    this.portaled?.remove();
    this.portaled = null;
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: Event): void {
    // A select, menu or picker inside the dialog closes first; it marks the
    // key as handled with preventDefault.
    if (!this.open || event.defaultPrevented) return;
    if (openModals[openModals.length - 1] !== this) return;
    if (!this.dismiss.observed) return;
    // A menu or listbox opened from inside the dialog is still showing: it
    // may close itself on this key from a document listener that runs after
    // this one, so leave Escape to it.
    if (this.dialogElement?.querySelector('[aria-haspopup][aria-expanded="true"]')) return;
    event.preventDefault();
    this.dismiss.emit();
  }

  onPointerDown(event: Event): void {
    this.pressStartedOnBackdrop = this.isBackdrop(event);
  }

  onBackdropClick(event: Event): void {
    const startedHere = this.pressStartedOnBackdrop;
    this.pressStartedOnBackdrop = false;
    if (!this.closeOnBackdrop || !startedHere || !this.isBackdrop(event)) return;
    this.dismiss.emit();
  }

  /**
   * The overlay and the full-width centring row are both "outside the panel".
   * Compared by identity, so a click on a nested dialog's scrim that bubbles
   * up here does not count as this dialog's backdrop.
   */
  private isBackdrop(event: Event): boolean {
    const target = event.target;
    return !!target && (target === event.currentTarget || target === this.dialogElement);
  }

  private register(): void {
    this.unregister();
    openModals.push(this);
  }

  private unregister(): void {
    const index = openModals.indexOf(this);
    if (index === -1) return;
    openModals.splice(index, 1);
    ariaOwnsRemove(this.dialogElement, TOAST_LIST_ID);
    if (this.isBrowser) syncToastOwner();
  }
}
